import { Track } from "@/src/lib/tracking";
/**
 * Positions Account Service - 持仓账户相关API服务
 *
 * 专门处理用户持仓账户的查询操作
 */

import { OspToast } from "@ospweb/sdk-ui";
import { GetUserPositionsResponse } from "./types";
import {
  AddPositionAsset,
  MemberClient,
  PositionAccountPlatformEnum,
  PositionAssetTypeEnum,
  RemovePositionAssetRequest,
  SyncPositionDataRequest,
} from "@features/api/memberApi";

import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/store/favorite/PositionsAccountService";

const I18n = I18nUtil.createScoped(Translations);

/**
 * Positions Account Service - 持仓账户服务
 */
export class PositionsAccountService {
  private memberApi: MemberClient<any>;

  constructor(memberApi: MemberClient<any>) {
    this.memberApi = memberApi;
  }

  /**
   * 获取用户持仓账户列表
   * @param options 查询选项
   * @returns 账户列表结果
   */
  public async getUserAccounts(options?: {
    platform?: PositionAccountPlatformEnum;
  }) {
    try {
      const response = await this.memberApi.positions.getUserAccounts({});
      if (response.data?.data) {
        // filter platform,扩展:后面 platform 可能为别的平台，not any more edgen
        let filteredRows = response?.data?.data?.rows;
        if (options?.platform) {
          filteredRows = filteredRows?.filter(
            (account) => account.platform === options.platform,
          );
        }

        // Sort: EDGEN platform accounts should be at index 0
        const sortedRows = filteredRows?.sort((a, b) => {
          if (a.platform === PositionAccountPlatformEnum.EDGEN) {
            return -1;
          }
          if (b.platform === PositionAccountPlatformEnum.EDGEN) {
            return 1;
          }
          return 0;
        });

        // Transform status from number to string
        const transformedData = {
          ...response.data.data,
          rows: sortedRows?.map((account) => ({
            ...account,
            status: account.status,
          })),
        };
        return {
          success: true,
          data: transformedData,
          message: response.data.msg,
        };
      } else {
        return {
          success: false,
          message: response.data?.msg || "error",
        };
      }
    } catch (error) {
      Track.error(error, {
        tag: "PositionsAccountService",
        msg: "获取用户持仓账户失败:",
      });
      return {
        success: false,
        message: "",
      };
    }
  }

  /**
   * 批量查询
   * Get user position assets
   * @param accountId Account ID
   * @param query Query options
   * @returns Position assets list result
   */
  public async getUserPositions(
    accountId: string,
    query?: {
      /** Data source category filter */
      sort?: "REPORT_360";
      /** Asset IDs */
      asset_ids?: string[];
      /** Asset type enumeration */
      asset_type?: PositionAssetTypeEnum;
      /**
       * The amount of positions requested from the APIs
       * @format int32
       * @max 20
       * @default 5
       */
      limit?: number;
      /** Pagination token */
      next_token?: string;
      /**
       * If you need to return enrich data
       * @default false
       */
      "enrich.enable"?: boolean;
      /**
       * If you need to return the price symbol
       * @default false
       */
      "enrich.with_base_data"?: boolean;
    },
  ): Promise<GetUserPositionsResponse> {
    try {
      const response = await this.memberApi.positions.getUserPositions(
        accountId,
        query,
      );
      if (response.data?.data) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.msg || "",
        };
      } else {
        return {
          success: false,
          message: response.data?.msg || "error",
        };
      }
    } catch (error) {
      Track.error(error, {
        tag: "PositionsAccountService",
        msg: "getUserPositions cause error:",
      });
      return {
        success: false,
        message: "",
      };
    }
  }

  /**
   * Add user position assets
   * @param accountId Account ID
   * @param data Position asset request data
   * @returns Operation result
   */
  public async addUserPosition(
    accountId: string,
    assets: AddPositionAsset[],
  ): Promise<any> {
    try {
      const response = await this.memberApi.positions.addUserPosition(
        accountId,
        assets,
      );
      if (response.data) {
        return {
          success: true,
          message: response.data.msg || "",
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          message: "error",
        };
      }
    } catch (error: any) {
      if (error?.error?.code === 146092) {
        OspToast.error({
          content: I18n.t(
            "Exceeds the maximum number of assets allowed in the portfolio (Max 50)",
          ),
        });
      }
      return {
        success: false,
        message: "",
      };
    }
  }

  /**
   * Remove user position asset
   * @param accountId Account ID
   * @param data Remove position asset request data
   * @returns Operation result
   */
  public async removeUserPosition(
    accountId: string,
    data: RemovePositionAssetRequest,
  ): Promise<any> {
    try {
      const response = await this.memberApi.positions.removeUserPosition(
        accountId,
        data,
      );
      if (response.data) {
        return {
          success: true,
          message: response.data.msg || "",
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          message: "error",
        };
      }
    } catch (error) {
      Track.error(error, {
        tag: "PositionsAccountService",
        msg: "removeUserPosition cause error:",
      });
      return {
        success: false,
        message: "",
      };
    }
  }

  /**
   * Delete position account
   * @param accountId Account ID
   * @returns Operation result
   */
  public async deletePositionAccount(accountId: string): Promise<any> {
    try {
      const response =
        await this.memberApi.positions.deletePositionAccount(accountId);
      if (response.data) {
        return {
          success: true,
          message: response.data.msg || "",
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          message: "error",
        };
      }
    } catch (error) {
      Track.error(error, {
        tag: "PositionsAccountService",
        msg: "deletePositionAccount cause error:",
      });
      return {
        success: false,
        message: "",
      };
    }
  }

  /**
   * Sync position data from third-party platform
   * @param accountId Account ID
   * @param data Sync request data
   * @returns Operation result
   */
  public async syncPositionData(
    accountId: string,
    data?: SyncPositionDataRequest,
  ): Promise<any> {
    try {
      const response = await this.memberApi.positions.syncPositionData(
        accountId,
        {
          sync_assets: true,
          sync_orders: true,
          force_refresh: true,
          ...data,
        },
      );
      if (response.data) {
        return {
          success: true,
          message: response.data.msg || "",
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          message: "error",
        };
      }
    } catch (error: any) {
      Track.error(error, {
        tag: "PositionsAccountService",
        msg: "syncPositionData cause error:",
      });
      // 提取错误信息：HTTP 状态码和错误码
      const status = error?.status;
      const errorCode = error?.error?.code;
      return {
        success: false,
        message: "",
        error: {
          status,
          code: errorCode,
        },
      };
    }
  }

  /**
   * Get position brokers list
   * @returns Brokers list result
   */
  public async getPositionBrokers(): Promise<any> {
    try {
      const response = await this.memberApi.positions.getPositionBrokers();
      if (response.data) {
        return {
          success: true,
          message: response.data.msg || "",
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          message: "error",
        };
      }
    } catch (error) {
      Track.error(error, {
        tag: "PositionsAccountService",
        msg: "getPositionBrokers cause error:",
      });
      return {
        success: false,
        message: "",
      };
    }
  }
}
